import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiSend,
  FiX,
  FiMessageSquare,
  FiBookOpen,
  FiZap,
  FiLink,
  FiFileText,
  FiAlertCircle,
  FiLoader,
  FiPlus,
  FiCopy,
  FiDownload,
  FiRefreshCw,
  FiMoreVertical,
  FiMinus
} from 'react-icons/fi';
import ChatMarkdown from './ChatMarkdown';
import styles from '../styles/documentChatV2.module.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const loadingMessages = [
  'Reading through the documents...',
  'Analyzing content...',
  'Thinking...',
  'Formulating response...'
];

const DocumentChatV2 = ({ documents: initialDocuments, onClose, onMinimize, allDocuments = [], onOpenDocument, isMinimized = false }) => {
  const [documents, setDocuments] = useState(initialDocuments || []);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [taskType, setTaskType] = useState('general');
  const [conversationId] = useState(`doc-chat-${Date.now()}`);
  const [showAddDocs, setShowAddDocs] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Reading through the documents...');
  
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Rotate loading messages
  useEffect(() => {
    if (!isLoading) return;
    
    let index = 0;
    const interval = setInterval(() => {
      index = (index + 1) % loadingMessages.length;
      setLoadingMessage(loadingMessages[index]);
    }, 2000);
    
    return () => clearInterval(interval);
  }, [isLoading]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [input]);

  const handleSend = async (e, predefinedMessage = null) => {
    if (e) e.preventDefault();
    
    const messageToSend = predefinedMessage || input.trim();
    if (!messageToSend || isLoading) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: messageToSend,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setError(null);

    try {
      // Prepare documents for API
      const documentContexts = documents.map(doc => ({
        name: doc.name,
        content: doc.content || doc.text || '',
        pageRange: doc.pageRange || ''
      }));

      const response = await fetch(`${API_BASE}/api/chat_with_documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: messageToSend,
          documents: documentContexts,
          history: messages,
          taskType: taskType,
          conversationId: conversationId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();

      const aiMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: data.response,
        citations: data.citations,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (err) {
      setError(err.message || 'Failed to send message');
      console.error('Document chat error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const quickActions = [
    {
      id: 'citations',
      label: 'Citations',
      icon: <FiLink size={14} />,
      message: 'Extract all citations and references from the documents, organized by relevance.',
      taskType: 'citations',
      tooltip: 'Extract and format all citations'
    },
    {
      id: 'insights',
      label: 'Insights',
      icon: <FiZap size={14} />,
      message: 'What are the key insights, themes, and main takeaways from these documents?',
      taskType: 'insights',
      tooltip: 'Analyze themes and findings'
    },
    {
      id: 'summary',
      label: 'Summary',
      icon: <FiFileText size={14} />,
      message: 'Provide a comprehensive summary of the main points in these documents.',
      taskType: 'general',
      tooltip: 'Get a concise summary'
    },
    {
      id: 'compare',
      label: 'Compare',
      icon: <FiBookOpen size={14} />,
      message: 'Compare and contrast the key arguments and findings across these documents.',
      taskType: 'insights',
      tooltip: 'Compare documents'
    }
  ];

  const addDocument = (doc) => {
    if (!documents.some(d => d.id === doc.id)) {
      setDocuments(prev => [...prev, doc]);
    }
    setShowAddDocs(false);
  };

  const removeDocument = (docId) => {
    setDocuments(prev => prev.filter(d => d.id !== docId));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const exportChat = () => {
    const chatText = messages.map(m => 
      `${m.role.toUpperCase()}: ${m.content}`
    ).join('\n\n');
    const blob = new Blob([chatText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-export-${Date.now()}.txt`;
    a.click();
  };

  const handleQuickAction = (action) => {
    setTaskType(action.taskType);
    handleSend(null, action.message);
  };

  const clearChat = () => {
    if (window.confirm('Clear all messages?')) {
      setMessages([]);
    }
  };

  if (isMinimized) {
    return null;
  }

  return (
    <div className={styles.overlay}>
      <motion.div
        className={styles.container}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.2 }}
      >
        {/* Sophisticated Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerLeft}>
              <div className={styles.iconWrapper}>
                <FiMessageSquare size={18} />
              </div>
              <div>
                <h2 className={styles.title}>Document Chat</h2>
                <span className={styles.subtitle}>{documents.length} {documents.length === 1 ? 'document' : 'documents'}</span>
              </div>
            </div>
            <div className={styles.headerRight}>
              {/* Options Dropdown */}
              <div className={styles.dropdown}>
                <button 
                  className={styles.optionsBtn}
                  onClick={() => setShowOptions(!showOptions)}
                  title="Options"
                >
                  <FiMoreVertical size={18} />
                </button>
                {showOptions && (
                  <div className={styles.dropdownMenu}>
                    <button onClick={exportChat}>
                      <FiDownload size={14} />
                      Export Chat
                    </button>
                    <button onClick={clearChat}>
                      <FiRefreshCw size={14} />
                      Clear Chat
                    </button>
                  </div>
                )}
              </div>
              <button className={styles.minimizeBtn} onClick={onMinimize || onClose} title="Minimize">
                <FiMinus size={18} />
              </button>
              <button className={styles.closeBtn} onClick={onClose} title="Close">
                <FiX size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Document Management Bar */}
        <div className={styles.docBar}>
          <div className={styles.docList}>
            {documents.map((doc, index) => (
              <div 
                key={index} 
                className={`${styles.docChip} ${onOpenDocument ? styles.docChipClickable : ''}`}
                onClick={() => onOpenDocument && onOpenDocument(doc)}
                title={onOpenDocument ? `Click to view ${doc.name}` : doc.name}
              >
                <FiFileText size={12} />
                <span className={styles.docName}>{doc.name}</span>
                {documents.length > 1 && (
                  <button
                    className={styles.removeDoc}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeDocument(doc.id);
                    }}
                    title="Remove from context"
                  >
                    <FiX size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
          {allDocuments && allDocuments.length > documents.length && (
            <button
              className={styles.addDocBtn}
              onClick={() => setShowAddDocs(!showAddDocs)}
              title="Add more documents"
            >
              <FiPlus size={14} />
              Add Docs
            </button>
          )}
        </div>

        {/* Add Documents Dropdown */}
        {showAddDocs && allDocuments && allDocuments.length > 0 && (
          <div className={styles.addDocsDropdown}>
            <div className={styles.addDocsHeader}>
              <span>Add to context</span>
              <button onClick={() => setShowAddDocs(false)}>
                <FiX size={14} />
              </button>
            </div>
            <div className={styles.addDocsList}>
              {allDocuments.filter(doc => !documents.some(d => d.id === doc.id)).map(doc => (
                <button
                  key={doc.id}
                  className={styles.addDocItem}
                  onClick={() => addDocument(doc)}
                >
                  <FiFileText size={14} />
                  <span>{doc.name}</span>
                  <FiPlus size={12} />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions Bar */}
        {messages.length === 0 && (
          <div className={styles.quickActionsBar}>
            {quickActions.map(action => (
              <button
                key={action.id}
                className={styles.quickAction}
                onClick={() => handleQuickAction(action)}
                disabled={isLoading}
                title={action.tooltip}
              >
                {action.icon}
                <span>{action.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* Messages Area */}
        <div className={styles.messages}>
          {messages.length === 0 ? (
            <div className={styles.emptyState}>
              <h3>Ask any questions you have about your document{documents.length > 1 ? 's' : ''}</h3>
              <p>Use the input below or try the quick actions above to get started</p>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`${styles.messageRow} ${message.role === 'user' ? styles.userRow : styles.aiRow}`}
                >
                  {message.role === 'assistant' && (
                    <div className={styles.avatar}>
                      <FiBookOpen size={14} />
                    </div>
                  )}
                  <div className={styles.bubble}>
                    {message.role === 'user' ? (
                      <p>{message.content}</p>
                    ) : (
                      <>
                        <ChatMarkdown content={message.content} />
                        {message.citations && message.citations.length > 0 && (
                          <div className={styles.citations}>
                            <div className={styles.citationsHeader}>
                              <FiLink size={12} />
                              <span>Citations</span>
                            </div>
                            <ul>
                              {message.citations.map((citation, idx) => (
                                <li key={idx}>{citation.text}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {message.role === 'assistant' && (
                    <button
                      className={styles.copyBtn}
                      onClick={() => copyToClipboard(message.content)}
                      title="Copy response"
                    >
                      <FiCopy size={12} />
                    </button>
                  )}
                </div>
              ))}
              {isLoading && (
                <div className={`${styles.messageRow} ${styles.aiRow}`}>
                  <div className={styles.avatar}>
                    <FiLoader size={14} className={styles.spin} />
                  </div>
                  <div className={styles.bubble}>
                    <p className={styles.loading}>{loadingMessage}</p>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Error Banner */}
        <AnimatePresence>
          {error && (
            <motion.div
              className={styles.error}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <FiAlertCircle size={14} />
              <span>{error}</span>
              <button onClick={() => setError(null)}>
                <FiX size={12} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Input Area */}
        <div className={styles.inputArea}>
          <div className={styles.inputRow}>
            <textarea
              ref={textareaRef}
              className={styles.input}
              placeholder="Ask about your documents..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend(e);
                }
              }}
              rows={1}
              disabled={isLoading}
            />
            <button
              className={styles.sendBtn}
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              title="Send message"
            >
              {isLoading ? <FiLoader size={16} className={styles.spin} /> : <FiSend size={16} />}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default DocumentChatV2;
